import { BaseShader } from "webgl-framework";
import { ShaderInputs } from "./ShaderInputs";
import { engine } from "../KotlinLib";

/**
 * The ghost: a textured mesh whose red and green channels pulse with their own looping timers,
 * faded into the same linear fog as the rest of the scene.
 *
 * The red and green are multiplied by the timer values, which run from 0 to 1, so the ghost
 * keeps its texture and swings through the colors as the two timers drift apart. The blue is
 * left as it is in the texture. The timers are read in the vertex shader and reach the pixels
 * as a tint, as they are the same for the whole mesh.
 *
 * Uniforms: [view_proj_matrix, sTexture, ghostTimers, ghostColor, fogColor, fogStartDistance,
 * fogDistance] - the order the ghost draw command puts them into.
 */
export class GhostShader extends BaseShader implements ShaderInputs {
    view_proj_matrix: WebGLUniformLocation | undefined;
    sTexture: WebGLUniformLocation | undefined;
    ghostTimers: WebGLUniformLocation | undefined;
    ghostColor: WebGLUniformLocation | undefined;
    fogColor: WebGLUniformLocation | undefined;
    fogStartDistance: WebGLUniformLocation | undefined;
    fogDistance: WebGLUniformLocation | undefined;

    rm_Vertex: number | undefined;
    rm_TexCoord0: number | undefined;

    public attributes?: number[];
    public typedAttributes?: { [key: number]: number };
    public uniforms?: WebGLUniformLocation[];

    /** @inheritdoc */
    fillCode() {
        this.vertexShaderCode = `precision highp float;
            uniform mat4 view_proj_matrix;
            uniform float fogStartDistance;
            uniform float fogDistance;
            // x = red of the ghost, y = green of it, both from their own looping timer.
            uniform vec2 ghostTimers;
            attribute vec4 rm_Vertex;
            attribute vec2 rm_TexCoord0;
            varying vec2 vTextureCoord;
            varying float vFogAmount;
            varying float vColorFade;
            varying float vAlphaFade;

            const float PI = 3.1415926535897932384626433832795;

            void main() {
              vec4 vertex = rm_Vertex;
              vTextureCoord = rm_TexCoord0;

              float u = vTextureCoord.x;
              float v = vTextureCoord.y;
              float offset = sin((u+ghostTimers.x) * PI * 4.0) + sin((u+ghostTimers.x) * PI * 12.0) * 0.5;
              if (v > 0.95) {
                vertex.z += offset * 0.12;
              }

              float frontCoeff = pow(-vertex.y + 1.6, 2.4) * 0.6;
              float squash = 1.0 + sin(ghostTimers.y * PI * 4.0);
              squash *= pow(smoothstep(0.25, 0.95, v) * 0.2, 2.0);
              squash *= frontCoeff;
              squash += 1.0;

              vertex.xy *= squash;

              vColorFade = (1.0 - smoothstep(0.2, 0.85, v)) * 0.3 + 0.7;
              vAlphaFade = ((1.0 - smoothstep(0.3, 0.95, v)) * 0.4 + 0.6) * (1.0 - vFogAmount);

              vertex.y += 0.6 * vColorFade * (sin(ghostTimers.x * PI * 2.0) + 1.0);

              gl_Position = view_proj_matrix * vertex;
              vFogAmount = clamp((length(gl_Position) - fogStartDistance) / fogDistance, 0.0, 1.0);
            }`;

        this.fragmentShaderCode = `precision mediump float;
            varying vec2 vTextureCoord;
            varying float vFogAmount;
            varying float vColorFade;
            varying float vAlphaFade;

            uniform sampler2D sTexture;
            uniform vec4 ghostColor;
            uniform vec4 fogColor;

            void main() {
              vec4 color = texture2D(sTexture, vTextureCoord);
              color *= vColorFade;
              color *= ghostColor;

              gl_FragColor = mix(color, fogColor, vFogAmount);

              gl_FragColor.a = vAlphaFade;
            //   gl_FragColor.a *= .0001; gl_FragColor.a += 0.9999;
            }`;
    }

    /** @inheritdoc */
    fillUniformsAttributes() {
        this.rm_Vertex = this.getAttrib("rm_Vertex");
        this.rm_TexCoord0 = this.getAttrib("rm_TexCoord0");

        this.view_proj_matrix = this.getUniform("view_proj_matrix");
        this.sTexture = this.getUniform("sTexture");
        this.ghostTimers = this.getUniform("ghostTimers");
        this.ghostColor = this.getUniform("ghostColor");
        this.fogColor = this.getUniform("fogColor");
        this.fogStartDistance = this.getUniform("fogStartDistance");
        this.fogDistance = this.getUniform("fogDistance");

        this.attributes = [this.rm_Vertex, this.rm_TexCoord0];
        this.typedAttributes = {
            [engine.AttributeType.VERTEX.ordinal]: this.rm_Vertex!,
            [engine.AttributeType.UV0.ordinal]: this.rm_TexCoord0!
        };
        this.uniforms = [
            this.view_proj_matrix,
            this.sTexture,
            this.ghostTimers,
            this.ghostColor,
            this.fogColor,
            this.fogStartDistance,
            this.fogDistance
        ];
    }
}
